/* The personal folder and the welcome email.
 *
 * Neither is Blueprint's to do. Creating a folder in Hub_MyDocs needs
 * Sites.Selected on the intranet site and sending from welcome@hird.co.uk needs
 * Mail.Send with an application access policy — both app-only permissions held
 * by func-hird-onboarding's managed identity, and both things a browser should
 * never be able to do on its own. So this asks the Function, as the signed-in
 * administrator, and the Function decides.
 *
 * THE SAME FUNCTION THE HUB CALLS
 *
 * Hub Admin has had this button for weeks and it works. Nothing here replaces
 * it; this is a second caller of the same endpoint, which is why the payload
 * and the status handling are deliberately the hub's and not an improvement on
 * them. The one difference is the depot — see below.
 *
 * WHAT THE STATUSES MEAN
 *
 *   200   folder and email both done, or folder already there and no email
 *   207   folder created, email did NOT send
 *   401   the Function did not accept the sign-in
 *   403   the caller does not hold the Onboarder role
 *
 * 207 is the one worth understanding. The Function only ever emails when the
 * folder was NEWLY created, which makes pressing the button twice safe — and
 * means that once a 207 has happened, no amount of pressing it again will send
 * that email. It has to go by hand. WelcomeSentOn is stamped only on a 200
 * precisely so the register keeps saying that job is outstanding.
 */

import type { TokenProvider } from '../sharepoint/GraphClient';

/** The Function's own words for why no email went, or a plain fallback. */
function describeEmail(email: unknown): string {
  return typeof email === 'string' && email ? email : 'the Function gave no reason';
}

export interface IOnboardRequest {
  /** The ACCOUNT, not the mailbox: simon.ford@hird.co.uk, not sf@. */
  upn: string;
  /** The mailbox, when there is one. Names and stamps the folder. */
  mail?: string;
  /** Where the welcome email goes: the address they actually read, often a personal one. */
  welcomeTo?: string;
  displayName?: string;
  /** Names the folder — "Jodie Farrar (Hull)". */
  depot?: string;
  /** Skip the email, for backfilling a folder somebody already needed. */
  folderOnly?: boolean;
  /** Send the welcome even if the folder was already there.
   *
   *  The Function only emails a NEWLY created folder, which is what makes
   *  pressing it twice safe. That was right while the folder and the welcome
   *  were one act. They are not: everybody was given hub access at once and is
   *  being welcomed by hand afterwards, so by now most people's folders have
   *  existed for a while. Without this they could never be welcomed at all. */
  sendWelcome?: boolean;
}

/** The email to HR when IT hands a starter over. */
export interface INotifyRequest {
  name: string;
  jobTitle?: string;
  depot?: string;
  start?: string;
  /** Company addresses, set under notify.hr in the Onboarding settings. */
  to: string[];
}

export interface INotifyOutcome {
  ok: boolean;
  sent: number;
  /** In words, for the person who pressed the button. Empty when it went. */
  message: string;
}

/** Where the email's link opens: the Hub, which is on the Function's list of allowed hosts. */
const HUB_LINK = 'https://hub.hird.co.uk/';

export interface IOnboardOutcome {
  ok: boolean;
  /** Said to the person who pressed the button, in words they can act on. */
  message: string;
  /** True only when the email actually sent, which is what may be stamped. */
  emailSent: boolean;
}

interface FunctionAnswer {
  folder?: { created?: boolean; folder?: string; error?: string };
  email?: string | { error?: string };
  error?: string;
}

export class OnboardingClient {
  constructor(
    private readonly getToken: TokenProvider,
    /** The Function App's /api base. */
    private readonly baseUrl: string,
    /** The app registration the Function's Easy Auth validates against — the
     *  hub's. A token for Blueprint's own client id is refused with a 401 that
     *  reads like an expired session. */
    private readonly hubClientId: string,
  ) {}

  async onboard(req: IOnboardRequest): Promise<IOnboardOutcome> {
    return this.call('onboard', req);
  }

  async offboard(upn: string, mail?: string): Promise<IOnboardOutcome> {
    return this.call('offboard', { upn, mail });
  }

  /** Tells HR somebody is ready to onboard. A failure comes back as words: mail never holds up a hand-over. */
  async notify(req: INotifyRequest): Promise<INotifyOutcome> {
    if (!this.baseUrl || !this.hubClientId) return { ok: false, sent: 0, message: 'the onboarding service is not configured in this build' };
    try {
      const token = await this.getToken([`api://${this.hubClientId}/access_as_user`]);
      const res = await fetch(`${this.baseUrl}/notify`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ kind: 'readyForHr', formTitle: '', ...req, link: HUB_LINK }),
      });
      const body = (await res.json().catch(() => null)) as { sent?: number; error?: string } | null;
      if (res.status === 200) return { ok: true, sent: typeof body?.sent === 'number' ? body.sent : req.to.length, message: '' };
      if (res.status === 401) return { ok: false, sent: 0, message: 'the onboarding service did not accept the sign-in' };
      if (res.status === 403) return { ok: false, sent: 0, message: 'this account needs the Onboarder role (SG_Hird Hub L2 Admin)' };
      // An older Function does not know this email: it needs republishing, which is not what the settings say.
      if (res.status === 400 && /^kind must be/.test(body?.error ?? '')) return { ok: false, sent: 0, message: 'the onboarding service has not been updated yet: republish func-hird-onboarding so it can send this email' };
      return { ok: false, sent: 0, message: body?.error || `the service answered ${res.status}` };
    } catch (err) {
      return { ok: false, sent: 0, message: msg(err) };
    }
  }

  private async call(path: string, body: unknown): Promise<IOnboardOutcome> {
    /* Not configured is its own answer. Without this the request goes to
       "undefined/onboard", fails to parse, and reports a network error — which
       sends somebody looking at the Function App rather than at the build. */
    if (!this.baseUrl || !this.hubClientId) {
      return {
        ok: false,
        emailSent: false,
        message:
          'The onboarding service is not configured in this build. VITE_ONBOARD_URL and ' +
          'VITE_HUB_CLIENT_ID have to be set — Hub Admin can still set people up in the meantime.',
      };
    }

    let token: string;
    try {
      token = await this.getToken([`api://${this.hubClientId}/access_as_user`]);
    } catch (err) {
      return {
        ok: false,
        emailSent: false,
        message: `Could not get permission to call the onboarding service: ${msg(err)}`,
      };
    }

    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}/${path}`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (err) {
      /* Nearly always CORS rather than the network, and it has been before: the
         Function accepts the token happily and the browser never sees the
         answer. Worth naming, because "failed to fetch" sends people to check
         whether the Function is running when it is.

         It must name THIS ORIGIN and the endpoint, though. The message used to
         say only "has to allow this site as an origin", which sent somebody to
         check an allow-list that already had the live site on it — the site
         they were actually on was a different one. The allow-list is exact:
         a preview environment, a custom domain and localhost are each a
         separate entry, and none of them is covered by the production URL. */
      const origin = typeof location === 'undefined' ? '(unknown)' : location.origin;
      return {
        ok: false,
        emailSent: false,
        message:
          `Could not reach the onboarding service at ${this.baseUrl}/${path}: ${msg(err)}. `
          + `If it is running, this is usually CORS — the Function App's allowed origins `
          + `must include exactly ${origin}.`,
      };
    }

    const answer = (await res.json().catch(() => null)) as FunctionAnswer | null;
    return describe(res.status, answer, path);
  }
}

/** The Function's two-part answer, in one sentence somebody can act on. */
export function describe(
  status: number,
  body: FunctionAnswer | null,
  path = 'onboard',
): IOnboardOutcome {
  if (status === 401) {
    return {
      ok: false,
      emailSent: false,
      message: 'The onboarding service did not accept the sign-in. Try signing out and back in.',
    };
  }
  if (status === 403) {
    return {
      ok: false,
      emailSent: false,
      message:
        'The account Blueprint is signed in as cannot set somebody up or close their folder. ' +
        'It needs the Onboarder role, which comes from SG_Hird Hub L2 Admin. ' +
        'Check which account is showing at the bottom left, and sign out and back in as the right one.',
    };
  }

  if (path === 'offboard' && status >= 200 && status < 300) {
    return { ok: true, emailSent: false, message: 'Their access to their own folder has been removed. The folder and everything in it stays.' };
  }

  const folder = body?.folder;

  /* 207 is the half-done case and the only one that leaves a job behind. The
     Function emails only on a newly created folder, so this email can never be
     sent by pressing the button again — it has to go by hand, and saying so is
     the difference between a job somebody does and a job nobody knows about. */
  if (status === 207) {
    const why = typeof body?.email === 'object' ? body.email?.error : undefined;
    return {
      ok: true,
      emailSent: false,
      message:
        `Folder created, but the welcome email did not send${why ? `: ${why}` : ''}. ` +
        'Pressing this again will not send it — the email only goes out with a new folder, ' +
        'so that one has to be sent by hand.',
    };
  }

  if (status >= 200 && status < 300 && folder) {
    const made = folder.created === true;
    /* What the Function says it did, not what this client infers from the folder. The
       two came apart with sendWelcome: an existing folder is emailed when asked, and
       "no new folder, so no email" was reported for an email that had gone, and the
       date it went was never recorded. */
    const sent = body?.email === 'sent';
    return {
      ok: true,
      emailSent: sent,
      message: sent
        ? made
          ? 'Folder created and the welcome email has been sent.'
          : 'Folder was already there. The welcome email has been sent.'
        : made
          ? `Folder created, but no email went (${describeEmail(body?.email)}).`
          : `Folder was already there, and no email went (${describeEmail(body?.email)}).`,
    };
  }

  return {
    ok: false,
    emailSent: false,
    message: folder?.error ?? body?.error ?? `The onboarding service answered ${status}.`,
  };
}

function msg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
